import { BenchmarkingSection } from '@/components/benchmarking-section';
import { CommonSectionOnboarding } from '@/components/common-section-onboarding';
import { CommonSectionSummary } from '@/components/common-section-summary';
import { Footer } from '@/components/footer';
import { FooterCtaSection } from '@/components/footer-cta-section';
import { Text } from '@/components/text';
import { View } from '@/components/ui/tw-wrappers';
import { SCREEN_MAX_WIDTH } from '@/constants/layout';
import { Colors } from '@/constants/theme';
import { useAnimatedHeader } from '@/hooks/use-animated-header';
import { useCountries } from '@/hooks/useCountries';
import { useBenchmarkingStore } from '@/stores/benchmarking-store';
import { Image } from 'expo-image';
import { useTranslation } from 'react-i18next';
import { StyleSheet, useWindowDimensions } from 'react-native';
import Animated, { FadeInUp } from 'react-native-reanimated';

export default function BenchmarkingScreen() {
  const { t } = useTranslation();

  const { countries, loading: countriesLoading, error: countriesError } = useCountries();

  const items = useBenchmarkingStore(s => s.items);
  const commonProps = useBenchmarkingStore(s => s.commonProps);
  const hasCommonProps = commonProps.vendorIdList.length > 0 && commonProps.invoicingAmount && commonProps.scenario;
  const { headerTranslateY, scrollHandler, totalHeight } = useAnimatedHeader();
  const { height: windowHeight } = useWindowDimensions();

  const showOnboarding = !hasCommonProps;

  return (
    <View style={styles.mainContainer}>
      {/* BACKGROUND (Fixed) */}
      <View style={[styles.backgroundContainer, { height: windowHeight }]}>
        <Image
          source={require('@/assets/images/background-l.webp')}
          style={styles.backgroundImage}
          contentFit='cover'
          alt={t('common.backgroundAlt', 'Abstract decorative background')}
        />
      </View>
      <Animated.ScrollView
        style={styles.scrollView}
        onScroll={scrollHandler}
        scrollEventThrottle={16}
        contentContainerStyle={[styles.scrollContent, { paddingTop: totalHeight }]}>
        {/* ONBOARDING SECTION */}
        {showOnboarding ? (
          <View key='onboarding' style={[styles.onboardingContainer, { minHeight: windowHeight - totalHeight }]}>
            <Animated.View
              style={styles.onboardingDescriptionContainer}
              entering={FadeInUp.delay(200).duration(800).springify()}>
              <Text style={styles.mainTitle} type='defaultBold'>
                {t('screens.benchmarking.title')}
              </Text>
              <Text style={styles.onBoardingDescriptionText}>{t('screens.benchmarking.subtitle')}</Text>
              <Text style={styles.onBoardingDescriptionText}>{t('screens.benchmarking.description')}</Text>
            </Animated.View>
            <Animated.View entering={FadeInUp.delay(400).duration(800).springify()}>
              <CommonSectionOnboarding
                countries={countries}
                countriesLoading={countriesLoading}
                countriesError={countriesError}
              />
            </Animated.View>
          </View>
        ) : (
          <View key='results' style={[styles.onboardingContainer, { minHeight: windowHeight - totalHeight }]}>
            <Animated.View style={styles.descriptionContainer} entering={FadeInUp.delay(200).duration(800).springify()}>
              <View style={styles.resultsHeader}>
                <View className='w-full pb-5 gap-3 items-center md:items-start'>
                  <Text type='title' className='text-center md:text-left'>
                    {t('screens.benchmarking.title')}
                  </Text>
                  <Text style={styles.descriptionText} className='text-center md:text-left'>
                    {t('screens.benchmarking.subtitle')}
                  </Text>
                </View>
              </View>
            </Animated.View>
            <Animated.View entering={FadeInUp.delay(200).duration(800).springify()} style={styles.resultsContainer}>
              <View style={styles.cardsSection}>
            <View style={styles.summaryContainer}>
              <CommonSectionSummary countries={countries} />
            </View>
                {hasCommonProps &&
                  <View style={styles.outerContainer}>
                    <View style={styles.innerContainer}>
                      <BenchmarkingSection countries={countries} />
                    </View>
                  </View>}
              </View>
            </Animated.View>
          </View>
        )}
        {items.length > 0 && (
          <FooterCtaSection
            countries={countries}
            modelType='benchmarking'
          />
        )}
        <Footer />
      </Animated.ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    backgroundColor: '#E3E3E3',
  },
  scrollView: {
    flex: 1,
    width: '100%',
    backgroundColor: 'transparent',
  },
  scrollContent: {
    flexGrow: 1,
  },
  onboardingContainer: {
    justifyContent: 'flex-start',
    gap: 20,
  },
  resultsContainer: {
    width: '100%',
    alignSelf: 'center',
    flex: 1,
    gap: 34,
    zIndex: 1,
  },
  resultsHeader: {
    paddingHorizontal: 16,
    marginTop: 50,
    gap: 16,
    justifyContent: 'center',
    alignItems: 'flex-start',
    zIndex: 10,
  },
  descriptionText: {
    color: '#666',
  },
  cardsSection: {
    gap: 16,
  },
  summaryContainer: {
    width: '100%',
    maxWidth: SCREEN_MAX_WIDTH,
    alignSelf: 'center',
    paddingHorizontal: 16,
  },
  backgroundContainer: {
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 0,
  },
  backgroundImage: {
    flex: 1,
    width: '100%',
  },
  descriptionContainer: {
    width: '100%',
    maxWidth: SCREEN_MAX_WIDTH,
    alignSelf: 'center',
  },
  onboardingDescriptionContainer: {
    paddingHorizontal: 40,
    marginTop: 30,
    marginBottom: 10,
  },
  mainTitle: {
    fontSize: 30,
    textAlign: 'center',
    marginBottom: 15,
  },
  onBoardingDescriptionText: {
    textAlign: 'center',
    color: Colors.light.primary,
    fontSize: 16,
  },
  outerContainer: {
    width: '100%',
    backgroundColor: '#E3E3E3',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 34,
    paddingTop: 20,
    marginTop: 10,
    paddingBottom: 20,
  },
  innerContainer: {
    maxWidth: SCREEN_MAX_WIDTH,
    paddingHorizontal: 16,
    alignSelf: 'center',
    width: '100%',
    gap: 34,
  },
});
